import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import SubHeader from 'components/SubHeader';
import StatusBar from 'components/StatusBar';
import StatusTooltip from 'components/StatusTooltip';
import CheckInRoutes from './Components/CheckInRoutes';
import FooterBar from 'components/FooterBar';
import Spinner from 'components/Spinner';
import { useQuery } from '@apollo/client';
import { GET_GROUP_DETAILS_BY_GROUP_ID } from 'graphql/service';
import moment from 'moment-timezone';
import { TIME_ZONE } from 'constants/constant';
import { debouncedErrorMessage } from 'utils/messageUtil';
import {
  DEFAULT_CHECK_IN_HOUR,
  DEFAULT_CHECK_IN_MINUTE,
  DEFAULT_CUT_OFF_HOUR,
  DEFAULT_CUT_OFF_MINUTE
} from 'constants/constant';

const GroupCheckIn = (props) => {

  const navigate = useNavigate()
  const location = useLocation()
  const pathSegments = location.pathname?.split('/');
  const groupId = pathSegments[pathSegments.length - 1];
  const [groupStatus, setGroupStatus] = useState('')
  const [earlyCheckInBookings, setEarlyCheckInBookings] = useState([])

  const { data: groupData, loading, error } = useQuery(GET_GROUP_DETAILS_BY_GROUP_ID, {
    variables: { groupId: groupId },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const groupByID = groupData?.groupByID;
  const groupName = groupByID?.name ?? '';

  useEffect(() => {
    setGroupStatus(groupByID?.status ?? '');
    const bookings = groupByID?.bookings ?? [];
    if (!bookings.length) {
      setEarlyCheckInBookings([]);
      return;
    }

    const now = moment().tz(TIME_ZONE);

    const newEarlyCheckInBookings = bookings.filter((booking) => {
      const checkInStart = moment(booking.checkInDate)
        .tz(TIME_ZONE)
        .set({ hour: DEFAULT_CHECK_IN_HOUR, minute: DEFAULT_CHECK_IN_MINUTE, second: 0 });

      const cutOff = moment(booking.checkInDate)
        .tz(TIME_ZONE)
        .set({ hour: DEFAULT_CUT_OFF_HOUR, minute: DEFAULT_CUT_OFF_MINUTE, second: 0 });

      return now < checkInStart && cutOff <= now;
    });

    setEarlyCheckInBookings(newEarlyCheckInBookings);
  }, [groupByID]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);
  
  if (loading) return <Spinner />;

  return (
    <>
      <Helmet>
        <title>Group Check In - Rook Hotel PMS</title>
        <meta name="description" content="Group Check In - Rook Hotel PMS" />
      </Helmet>
      <SubHeader>
        <div className="title">
          <Link
            className="highlight"
            to='/bookings'
          >
            Bookings
          </Link>
          {` > ${groupName}`}
          <StatusBar status={groupStatus} />
          {groupStatus === "CANCELLED" ? (
            <StatusTooltip
              createdAt={groupData.groupByID.cancelledAt}
              createdBy={groupData.groupByID.cancelledBy?.fullName}
              type={"Cancellation"}
              reason={groupData.groupByID.cancellationReason}
              amount={groupData.groupByID.cancellationFee}
            />
          ) : null}
        </div>
      </SubHeader>
      <div style={{ marginTop: '100px' }}>
        <CheckInRoutes
          earlyCheckInBookings={earlyCheckInBookings}
          name={groupName}
          status={groupStatus}
          setStatus={setGroupStatus}
        />
      </div>
      <FooterBar />
    </>
  );
}

export default GroupCheckIn;